import { From } from './From'
import { To } from './To'
import { Form } from '@/ui/atoms/form/Form'
import { SwapMode, UseBridgeResults } from '@/features/bridge/logic/useBridge'
import { Button } from '@/ui/atoms/button/Button'
import * as hl from '@nktkas/hyperliquid'
import { BridgeObjective } from '@/features/actions/flavours/bridge/types'
import { useState } from 'react'
import { track, classifyError, triggerWalletConnect } from '@/features/analytics'
import { ToggleButton } from '@/features/swap/components/forms/swap-form/ToggleButton'
import { normalizeUnsupportedCharacters } from '@/utils/strings'
import { useAccount } from '@/domain/hooks/useAccount'
import { usePublicClient, useWalletClient } from 'wagmi'
import { erc20Abi, parseEther, parseUnits } from 'viem'
import { depositUSDC } from '../../logic/depositUSDC'
import { waitForTxReceipt } from '@/utils/waitForTxReceipt'

const transport = new hl.HttpTransport()

export function BridgeForm(props: UseBridgeResults & { setErrorMessage: (s: string) => void }) {
  const {
    form,
    guestMode,
    fromTokenField,
    toTokenField, // Commented out as it's unused
    openConnectModal,
    actions,
    pageStatus,
    swapDirection,
    swapMode,
    setErrorMessage,
  } = props

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(() => {})}>
        <input type="hidden" name="swapMode" value={swapMode} />
        <div className="relative z-10">
          <From
            control={form.control}
            assets={fromTokenField.availableAssets}
            selectedAsset={fromTokenField.selectedAsset}
            changeToken={fromTokenField.changeToken}
            maxSelectedFieldName={fromTokenField.maxSelectedFieldName}
            onChange={fromTokenField.onChange}
            onSetMax={fromTokenField.onSetMax}
            maxValue={fromTokenField.maxValue}
            swapMode={swapMode}
          />
          <ToggleButton
            className="absolute right-1/2 bottom-0 translate-x-1/2 translate-y-3/4"
            onClick={swapDirection}
          />
        </div>
        <To
          control={form.control}
          assets={toTokenField.availableAssets}
          selectedAsset={toTokenField.selectedAsset}
          changeToken={toTokenField.changeToken}
          maxSelectedFieldName={toTokenField.maxSelectedFieldName}
          onChange={toTokenField.onChange}
          swapMode={swapMode}
        />

        <div className="mt-4">
          <div className="mb-2 text-muted-foreground text-sm" />
          {guestMode ? (
            <Button className="w-full rounded-lg" onClick={() => void triggerWalletConnect(openConnectModal, 'bridge_form')}>
              Connect wallet
            </Button>
          ) : (
            <BridgeButton
              objectives={actions}
              onFinish={pageStatus.goToSuccessScreen}
              enabled={pageStatus.actionsEnabled}
              swapMode={swapMode}
              setErrorMessage={setErrorMessage}
            />
          )}
        </div>
      </form>
    </Form>
  )
}

function BridgeButton(props: {
  objectives: BridgeObjective[]
  onFinish: () => void
  enabled: boolean
  swapMode: SwapMode
  setErrorMessage: (s: string) => void
}) {
  const account = useAccount()
  const { data: walletClient } = useWalletClient()
  const publicClient = usePublicClient()

  const [isLoading, setIsLoading] = useState(false)
  const { setErrorMessage } = props

  async function handleClick() {
    setErrorMessage('')

    if (!account || !walletClient || !publicClient || !props.objectives[0]) {
      return
    }

    const objective = props.objectives[0]
    const fromSymbol = objective.from.token.symbol

    // Get tokenId from the objective or fallback to the mapping
    const tokenId = objective.from.tokenId

    if (!tokenId) {
      setErrorMessage(`Token ID not found for: ${fromSymbol}`)
      return
    }

    setIsLoading(true)

    const startTime = Date.now()
    track('bridge_submitted', {
      token: fromSymbol,
      amount: objective.amount.toString(),
      source_chain: props.swapMode === 'bridgeToHyperEVM' ? 'hypercore' : 'hyperevm',
      destination_chain: props.swapMode === 'bridgeToHyperEVM' ? 'hyperevm' : 'hypercore',
    })

    let currentStep: 'sign' | 'confirmation' = 'sign'
    try {
      if (props.swapMode === 'bridgeToHyperEVM') {
        // Use HyperLiquid client for bridging to HyperEVM
        const hlClient = new hl.ExchangeClient({
          wallet: walletClient,
          transport,
        })

        const tokenString = `${String(normalizeUnsupportedCharacters(fromSymbol, { uppercase: true }))}:${tokenId}`

        const data = await hlClient.spotSend({
          destination: objective.from.systemAddress as `0x${string}`,
          amount: objective.amount.toString(),
          token: tokenString as `${string}:0x${string}`,
        })

        if (data.status === 'ok') {
          track('bridge_completed', {
            token: fromSymbol,
            amount: objective.amount.toString(),
            source_chain: 'hypercore',
            destination_chain: 'hyperevm',
            duration_ms: Date.now() - startTime,
          })
          props.onFinish()
        } else {
          throw new Error(`Transfer failed: ${data.status}`)
        }
      } else {
        const tokenAddress = objective.from.token.address as `0x${string}`

        // USDC requires a special approval -> deposit flow to bridge to HyperCore
        // depositContract contains the bridge contract address (evmContract is the real ERC20)
        if (fromSymbol === 'USDC' && objective.from.depositContract) {
          const bridgeContractAddress = objective.from.depositContract as `0x${string}`

          const amountInBaseUnits = parseUnits(objective.amount.toString(), objective.from.token.decimals)

          await depositUSDC(walletClient, publicClient, tokenAddress, bridgeContractAddress, amountInBaseUnits)

          track('bridge_completed', {
            token: fromSymbol,
            amount: objective.amount.toString(),
            source_chain: 'hyperevm',
            destination_chain: 'hypercore',
            duration_ms: Date.now() - startTime,
          })
          props.onFinish()
          return
        }

        // For bridging to HyperCore, use wallet sendBalance
        const token =
          objective.from.token.symbol === 'HYPE'
            ? undefined
            : {
                address: tokenAddress,
                decimals: objective.from.token.decimals,
              }

        let tx: `0x${string}`

        if (token) {
          tx = await walletClient.writeContract({
            address: tokenAddress,
            abi: erc20Abi,
            functionName: 'transfer',
            args: [
              objective.from.systemAddress as `0x${string}`,
              parseUnits(objective.amount.toString(), token.decimals),
            ],
          })
        } else {
          tx = await walletClient.sendTransaction({
            to: objective.from.systemAddress as `0x${string}`,
            value: parseEther(objective.amount.toString()),
          })
        }

        track('bridge_signed', {
          token: fromSymbol,
          amount: objective.amount.toString(),
          source_chain: 'hyperevm',
          destination_chain: 'hypercore',
          tx_hash: tx,
        })

        currentStep = 'confirmation'
        await waitForTxReceipt(publicClient, tx)

        track('bridge_completed', {
          token: fromSymbol,
          amount: objective.amount.toString(),
          source_chain: 'hyperevm',
          destination_chain: 'hypercore',
          tx_hash: tx,
          duration_ms: Date.now() - startTime,
        })

        props.onFinish()
      }
    } catch (error) {
      track('bridge_failed', {
        token: fromSymbol,
        amount: objective.amount.toString(),
        source_chain: props.swapMode === 'bridgeToHyperEVM' ? 'hypercore' : 'hyperevm',
        destination_chain: props.swapMode === 'bridgeToHyperEVM' ? 'hyperevm' : 'hypercore',
        error_type: classifyError(error),
        error_message: error instanceof Error ? error.message : 'Transaction Failed',
        failed_at: currentStep,
      })
      console.error(error)
      setErrorMessage(
        error instanceof Error
          ? error.message.includes('User rejected')
            ? 'User rejected the request'
            : error.message
          : 'Transaction Failed',
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="relative pb-1">
      <Button className="w-full rounded-lg" disabled={!props.enabled || isLoading} onClick={handleClick}>
        {isLoading
          ? 'Transferring...'
          : `Transfer ${props.objectives[0]?.from.token.symbol || 'Token'} to ${props.swapMode === 'bridgeToHyperEVM' ? 'HyperEVM' : 'HyperCore'}`}
      </Button>
    </div>
  )
}
